import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import * as api from '../api.ts';
import {
  Directory,
  type DirectoryType,
  InspectField,
  InspectMuted,
  InspectOverlay,
} from '../directory.tsx';
import { ClockIcon } from '../icons.tsx';
import { Events, Reports } from './events.tsx';
import { Button, Cell, Empty, LoadError, Row, Select, Spinner, Table } from '../ui.tsx';

/**
 * Activity — its own rail directory, on the shared rail-directory shell
 * (`directory.tsx`): **one filterable log of everything that has happened, and
 * the place the signed reports are generated**, given the same type-column +
 * sticky-header + right-inspect-overlay shape as the other rail screens.
 *
 * It is composed from the surfaces that already existed rather than a new data
 * source (G20 — nothing here invents activity):
 *
 * - `Events` (events.tsx) is the drills-and-incidents feed with its drill/live
 *   filter, the settled escalations, and **inline signed-report generation** — so
 *   report generation is reachable straight from this screen. Opening an event
 *   surfaces its signed record in `Reports` beneath. Composed `embedded`, so the
 *   shared header owns the "Activity" title.
 * - `ChangeLog` reads the append-only audit trail (`getAudit`) — the record of
 *   the roster and configuration changes, stand-downs, acknowledgements and the
 *   rest. It moved here from Settings' Security tab because a log of what
 *   happened is a record you review on Activity, not org configuration. The
 *   header's search narrows it, it scrolls within its own bound, and a row opens
 *   its full entry in the **right inspect overlay**.
 *
 * The **type column** slices the one log by what the reader is after: everything,
 * the evacuations and drills, the people-and-roster changes, the reports, or the
 * raw change log. All of it is a different lens on Events + Reports + ChangeLog,
 * never a new query.
 */

const TYPES: DirectoryType[] = [
  { id: 'all', label: 'All activity' },
  { id: 'events', label: 'Evacuations & drills' },
  { id: 'people', label: 'People' },
  { id: 'reports', label: 'Reports' },
  { id: 'changes', label: 'Change log' },
];

export function Activity({
  orgId,
  role,
  onOpenLive,
  evidenceEnabled,
  onOpenEvidence,
}: {
  orgId: string;
  role: api.Role;
  /** Open the full-bleed live command view for a running event. */
  onOpenLive: (eventId: string) => void;
  /** Whether this org has the Evidence module — bridges from a signed record. */
  evidenceEnabled?: boolean;
  onOpenEvidence?: () => void;
}) {
  // The event whose signed record is open beneath the feed. Set by opening an
  // event's report from the Events table — the same wiring the screen carried
  // when it lived inline in main.tsx.
  const [reportEventId, setReportEventId] = useState<string | null>(null);

  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  // The per-screen Site filter (owner decision 2026-09-08): the global rail site
  // switcher is gone, so the site-scoped part of Activity — the drills-and-
  // incidents feed — carries its own. The change log is the org-wide audit trail
  // and is not site-scoped, so the filter only applies while events are in view.
  const [site, setSite] = useState('');
  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });

  const showEvents = type === 'all' || type === 'events' || type === 'reports';
  const showChangeLog = type === 'all' || type === 'people' || type === 'changes';
  const onlyPeople = type === 'people';

  const siteList = sites.data?.sites ?? [];
  const siteOptions = [
    { value: '', label: 'All sites' },
    ...siteList.map((s) => ({ value: s.id, label: s.name })),
  ];
  // The events feed carries a site *name* per row, not an id, so the filter
  // resolves the chosen building's name and Events narrows by that (no API
  // change — this is a UI-only filter over the data already fetched).
  const siteName = site ? (siteList.find((s) => s.id === site)?.name ?? null) : null;

  return (
    <Directory
      title="Activity"
      typeColumnLabel="Activity types"
      types={TYPES}
      activeType={type}
      onSelectType={setType}
      // The search narrows the change log; it is offered only in the views that
      // carry it (the events feed keeps its own drill/live filter).
      search={
        showChangeLog
          ? {
              value: search,
              onChange: setSearch,
              label: 'Search changes',
              placeholder: 'Action, who, or detail',
            }
          : undefined
      }
      // The Site filter narrows the drills-and-incidents feed to one building; it
      // is offered only while events are in view (the change log is org-wide) and
      // only once the org has more than one building to choose between.
      //
      // On the combined "all activity" lens the change log sits beneath the feed
      // and is NOT site-scoped (it is the org-wide audit trail) — so a narrowed
      // feed above an unchanged log would read as a half-working filter. Say the
      // scope in one line, but only in the lens where both are on screen; in the
      // events-only lenses the filter scopes everything visible and needs no note.
      filters={
        showEvents && siteList.length > 1 ? (
          <div className="w-40">
            <Select label="Site" value={site} onChange={setSite} options={siteOptions} />
            {showChangeLog ? (
              <p className="mt-1 text-[11px] leading-[15px] text-[var(--ink-3)]">
                Narrows drills &amp; incidents; the change log is org-wide.
              </p>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {showEvents ? (
        <>
          {/*
            The drills-and-incidents feed: every event, its state, who answered,
            and the signed report it produced — generated inline from the row.
          */}
          <Events
            orgId={orgId}
            role={role}
            onOpen={onOpenLive}
            onOpenReport={setReportEventId}
            siteName={siteName}
            embedded
          />

          {reportEventId ? (
            <div className="mt-10 border-t border-[var(--line)] pt-8">
              <Reports
                orgId={orgId}
                eventId={reportEventId}
                evidenceEnabled={evidenceEnabled}
                onOpenEvidence={onOpenEvidence}
              />
            </div>
          ) : null}
        </>
      ) : null}

      {showChangeLog ? (
        <div className={showEvents ? 'mt-10 border-t border-[var(--line)] pt-8' : undefined}>
          {/*
            The wider record: roster and configuration changes, stand-downs and
            acknowledgements — the append-only audit trail, searched from the
            header and bounded so a long history scrolls rather than dumps. A row
            opens its full entry in the right inspect overlay.
          */}
          <ChangeLog orgId={orgId} query={search} onlyPeople={onlyPeople} />
        </div>
      ) : null}
    </Directory>
  );
}

/** The audit actions that are about people and the roster, for the
 *  "People & roster" lens. Matched on the action's prefix, because the audit
 *  action is a dotted namespace (`roster.import`, `membership.role_changed`). */
const PEOPLE_ACTION_PREFIXES = [
  'roster',
  'membership',
  'person',
  'people',
  'role',
  'team',
  'guardian',
  'invitation',
  'invite',
];

function isPeopleAction(action: string): boolean {
  const head = action.split('.')[0] ?? action;
  return PEOPLE_ACTION_PREFIXES.includes(head);
}

/**
 * The change log — the append-only audit trail as a searchable, scrollable feed,
 * with each row's full entry reachable in the right inspect overlay.
 *
 * The same data Settings' Security tab used to show, promoted here as part of the
 * one activity record. Search (owned by the shared header) narrows client-side
 * across the action, who did it and the detail; the `onlyPeople` lens keeps only
 * the roster/people actions. The list is bounded to its own scroll so hundreds
 * of entries never push the rest of the screen off. Nothing is invented — each
 * row is an audit entry the server recorded, and the audit trail is PII-free by
 * construction (`getAudit`).
 */
function ChangeLog({
  orgId,
  query,
  onlyPeople,
}: {
  orgId: string;
  query: string;
  onlyPeople: boolean;
}) {
  const audit = useQuery({ queryKey: ['audit', orgId], queryFn: () => api.getAudit(orgId) });
  const [openId, setOpenId] = useState<string | number | null>(null);

  const entries = audit.data?.entries ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (onlyPeople && !isPeopleAction(e.action)) return false;
      if (!q) return true;
      const haystack = [e.action, e.actor ?? '', e.detail ? JSON.stringify(e.detail) : '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, query, onlyPeople]);

  const openEntry = openId != null ? (entries.find((e) => e.id === openId) ?? null) : null;

  return (
    <>
      {/* No inner "Changes" section title and no boxed scroll (owner fix
          2026-09-07): the type column already names this lens and the shared
          header owns the title + search, so the list renders edge-to-edge and
          the whole content region scrolls under the sticky header (People's
          model). */}
      <p className="mb-4 max-w-[620px] text-[13px] text-[var(--ink-2)]">
        Every recorded change — roster edits, stand-downs, acknowledgements and configuration — from
        the append-only audit trail. Search from the header to narrow it; a row opens its full
        entry.
      </p>

      {audit.isError && !audit.data ? (
        <LoadError onRetry={() => void audit.refetch()}>
          Couldn’t load the change log. This is a load error, not an empty history — retry.
        </LoadError>
      ) : audit.isLoading || !audit.data ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <Empty>Nothing has been changed yet.</Empty>
      ) : filtered.length === 0 ? (
        <Empty>
          {onlyPeople
            ? 'No people changes match this view.'
            : `Nothing matches “${query}”. Clear the search to see every change.`}
        </Empty>
      ) : (
        <Table head={['When', 'Action', 'Who', 'Detail', '']}>
          {filtered.map((e) => (
            <Row key={e.id} active={openId === e.id}>
              <Cell>
                <span className="flex items-center gap-2.5">
                  {/* Leading event glyph in currentColor — a change is a
                      logged event; the timestamp beside it is the content. */}
                  <span aria-hidden className="shrink-0 text-[var(--ink-2)]">
                    <ClockIcon size={16} />
                  </span>
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </Cell>
              <Cell>
                <span className="mono text-[13px]">{e.action}</span>
              </Cell>
              <Cell>{e.actor ?? '—'}</Cell>
              <Cell wide>
                <span className="mono text-[12px] text-[var(--ink-2)]">
                  {e.detail ? JSON.stringify(e.detail) : '—'}
                </span>
              </Cell>
              <Cell>
                <Button
                  variant="quiet"
                  label={`View the change ${e.action} at ${new Date(e.created_at).toLocaleString()}`}
                  onClick={() => setOpenId(e.id)}
                >
                  Details
                </Button>
              </Cell>
            </Row>
          ))}
        </Table>
      )}

      {openEntry ? (
        <InspectOverlay
          ariaLabel={`Change: ${openEntry.action}`}
          title={openEntry.action}
          subtitle={new Date(openEntry.created_at).toLocaleString()}
          onClose={() => setOpenId(null)}
        >
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2.5 text-[14px]">
            <InspectField label="When">
              {new Date(openEntry.created_at).toLocaleString()}
            </InspectField>
            <InspectField label="Action">
              <span className="mono text-[13px]">{openEntry.action}</span>
            </InspectField>
            <InspectField label="Who">
              {openEntry.actor ?? <InspectMuted>—</InspectMuted>}
            </InspectField>
          </dl>

          <div className="mt-6 border-t border-[var(--line)] pt-5">
            <h3 className="mb-3 text-[13px] font-[650] tracking-[0.04em] text-[var(--ink-2)] uppercase">
              Detail
            </h3>
            {openEntry.detail ? (
              <pre className="mono overflow-x-auto rounded-[var(--radius-1)] bg-[var(--paper-3)] p-3 text-[12px] leading-[18px] text-[var(--ink)]">
                {JSON.stringify(openEntry.detail, null, 2)}
              </pre>
            ) : (
              <p className="text-[13px] text-[var(--ink-2)]">
                No further detail was recorded for this change.
              </p>
            )}
          </div>
        </InspectOverlay>
      ) : null}
    </>
  );
}
